import {
  collection,
  deleteDoc,
  doc,
  enableIndexedDbPersistence,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebaseConfig";

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  address: string;
  maxTickets: number;
  isClosedEvent?: boolean; 
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface EventContextType {
  activeEventIds: string[];
  events: Event[];
  activeEvents: Event[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchEventById: (eventId: string) => Promise<Event | null>;
  createEvent: (
    eventData: Omit<Event, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  deleteEvent: (eventId: string) => Promise<void>;
  setEventActive: (eventId: string) => Promise<void>;
  setEventInactive: (eventId: string) => Promise<void>;
  setMultipleEventsActive: (eventIds: string[]) => Promise<void>;
  isEventActive: (eventId: string) => boolean;
  clearError: () => void;
  refreshEvents: () => Promise<void>;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeEventIds, setActiveEventIds] = useState<string[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [activeEvents, setActiveEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize Firestore with persistence
  useEffect(() => {
    const initFirestore = async () => {
      try {
        await enableIndexedDbPersistence(db);
      } catch (err: any) {
        if (err.code === "failed-precondition") {
          console.warn(
            "Multiple tabs open, persistence can only be enabled in one tab at a time."
          );
        } else if (err.code === "unimplemented") {
          console.warn("The current browser doesn't support persistence.");
        }
      }
    };

    initFirestore();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch active events
      const activeEventsDoc = await getDoc(doc(db, "activeEvents", "info"));
      const activeIds = activeEventsDoc.exists()
        ? activeEventsDoc.data().eventIds || []
        : [];

      // Create document if it doesn't exist
      if (!activeEventsDoc.exists()) {
        await setDoc(doc(db, "activeEvents", "info"), {
          eventIds: [],
          updatedAt: Timestamp.now(),
        });
      }

      setActiveEventIds(activeIds);

      // Fetch all events
      const eventsSnapshot = await getDocs(collection(db, "events"));
      const fetchedEvents: Event[] = eventsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Event)
      );

      // Sort events by date
      fetchedEvents.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setEvents(fetchedEvents);

      // Calculate active events
      const newActiveEvents = fetchedEvents
        .filter((event) => activeIds.includes(event.id))
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
      setActiveEvents(newActiveEvents);
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("Failed to load events. Please refresh the page.");
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  // Set up real-time listeners
  useEffect(() => {
    if (!initialized) {
      fetchInitialData();
      return;
    }

    const unsubscribeActiveEvents = onSnapshot(
      doc(db, "activeEvents", "info"),
      (doc) => {
        if (doc.exists()) {
          setActiveEventIds(doc.data().eventIds || []);
        }
      },
      (error) => {
        console.error("Error listening to active events:", error);
        setError("Failed to listen to active events");
      }
    );

    const unsubscribeEvents = onSnapshot(
      collection(db, "events"),
      (snapshot) => {
        const fetchedEvents: Event[] = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Event)
        );

        fetchedEvents.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        setEvents(fetchedEvents);
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to events:", error);
        setError("Failed to listen to events");
        setLoading(false);
      }
    );

    return () => {
      unsubscribeActiveEvents();
      unsubscribeEvents();
    };
  }, [initialized]);

  useEffect(() => {
    const newActiveEvents = events
      .filter((event) => activeEventIds.includes(event.id))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    setActiveEvents(newActiveEvents);
  }, [events, activeEventIds]);

  const fetchEventById = async (eventId: string): Promise<Event | null> => {
    try {
      const eventDoc = await getDoc(doc(db, "events", eventId));
      if (eventDoc.exists()) {
        return { id: eventDoc.id, ...eventDoc.data() } as Event;
      }
      return null;
    } catch (error) {
      console.error("Error fetching event:", error);
      setError(`Failed to fetch event ${eventId}`);
      return null;
    }
  };

  const createEvent = async (
    eventData: Omit<Event, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    try {
      const newEventRef = doc(collection(db, "events"));
      const timestamp = Timestamp.now();

      const fullEventData = {
        ...eventData,
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      await setDoc(newEventRef, fullEventData);
      return newEventRef.id;
    } catch (error) {
      console.error("Error creating event:", error);
      setError("Failed to create event");
      throw error;
    }
  };

  const deleteEvent = async (eventId: string): Promise<void> => {
    try {
      if (isEventActive(eventId)) {
        await setEventInactive(eventId);
      }

      await deleteDoc(doc(db, "events", eventId));

      const registrationsSnapshot = await getDocs(
        collection(db, `events/${eventId}/registrations`)
      );

      await Promise.all(
        registrationsSnapshot.docs.map((doc) => deleteDoc(doc.ref))
      );
    } catch (error) {
      console.error("Error deleting event:", error);
      setError("Failed to delete event");
      throw error;
    }
  };

  const setEventActive = async (eventId: string) => {
    try {
      if (activeEventIds.includes(eventId)) return;

      const eventDoc = await getDoc(doc(db, "events", eventId));
      if (!eventDoc.exists()) {
        throw new Error("Event not found");
      }

      const updatedActiveEvents = [...activeEventIds, eventId];
      await setDoc(doc(db, "activeEvents", "info"), {
        eventIds: updatedActiveEvents,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error setting event active:", error);
      setError("Failed to activate event");
    }
  };

  const setEventInactive = async (eventId: string) => {
    try {
      const updatedActiveEvents = activeEventIds.filter((id) => id !== eventId);
      await setDoc(doc(db, "activeEvents", "info"), {
        eventIds: updatedActiveEvents,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error setting event inactive:", error);
      setError("Failed to deactivate event");
    }
  };

  const setMultipleEventsActive = async (eventIds: string[]) => {
    try {
      const eventPromises = eventIds.map((id) => getDoc(doc(db, "events", id)));
      const eventDocs = await Promise.all(eventPromises);

      if (eventDocs.some((doc) => !doc.exists())) {
        throw new Error("One or more events not found");
      }

      await setDoc(doc(db, "activeEvents", "info"), {
        eventIds,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error setting multiple events active:", error);
      setError("Failed to update active events");
    }
  };

  const isEventActive = (eventId: string): boolean => {
    return activeEventIds.includes(eventId);
  };

  const clearError = () => {
    setError(null);
  };

  const refreshEvents = async () => {
    await fetchInitialData();
  };

  const value: EventContextType = {
    activeEventIds,
    events,
    activeEvents,
    loading,
    error,
    initialized,
    fetchEventById,
    createEvent,
    deleteEvent,
    setEventActive,
    setEventInactive,
    setMultipleEventsActive,
    isEventActive,
    clearError,
    refreshEvents,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export const useEventContext = () => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error("useEventContext must be used within an EventProvider");
  }
  return context;
};

export type { Event, EventContextType };

